import {
    cloneElement,
    type FC
} from "react";
import {
    type LayoutChangeEvent,
    type ViewStyle,
    StyleSheet,
    Platform,
    View
} from "react-native";
import type IDropdownMenuTriggerProps from "./type";
import type {
    DropdownMenuTriggerWebPropsType
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useDropdownMenuContext
} from "../../context";
import type {
    DropdownMenuTriggerElementType,
    DropdownMenuKeyboardEventType
} from "../../type";
import {
    DROPDOWN_MENU_ARROW_DOWN_KEY,
    DROPDOWN_MENU_ARROW_UP_KEY
} from "../../util";
import {
    getStateColorKey
} from "../../../../helpers/theme/state";
import StateLayer from "../../../stateLayer";
import Button from "../../../button";

const DropdownMenuTrigger: FC<IDropdownMenuTriggerProps> = ({
    isDisabled: isDisabledProp = false,
    customTheme: customThemeProp,
    children,
    style
}) => {
    const context = useDropdownMenuContext({
        part: "Trigger"
    });

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        radiuses,
        borders
    } = NCoreUIKitTheme.useContext(customTheme);

    if(!context) {
        return null;
    }

    const {
        handleTriggerWidthChange,
        handleOpenChange,
        anchorRef,
        contentId,
        triggerId,
        isOpen
    } = context;

    const handleDefaultPress = () => {};

    const childElement: DropdownMenuTriggerElementType = typeof children === "string" ? <Button
        onPress={handleDefaultPress}
        customTheme={customTheme}
        variant="outline"
        title={children}
        type="neutral"
        size="small"
    /> : children;

    const isButton = childElement.type === Button;

    const isDisabled = isDisabledProp || childElement.props.isDisabled === true;

    const handlePress = () => {
        childElement.props.onPress?.();

        if(isDisabled) {
            return;
        }

        handleOpenChange(!isOpen);
    };

    const onLayout = (event: LayoutChangeEvent) => {
        handleTriggerWidthChange(event.nativeEvent.layout.width);
    };

    const onKeyDown = (event: DropdownMenuKeyboardEventType) => {
        if(isDisabled || event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }

        if(event.key === DROPDOWN_MENU_ARROW_DOWN_KEY) {
            event.preventDefault();

            handleOpenChange(true, "first");

            return;
        }

        if(event.key === DROPDOWN_MENU_ARROW_UP_KEY) {
            event.preventDefault();

            handleOpenChange(true, "last");
        }
    };

    const webProps: DropdownMenuTriggerWebPropsType = Platform.OS === "web" ? {
        onKeyDown
    } : {};

    const renderOpenLayer = () => {
        if(!isButton || !isOpen || isDisabled) {
            return null;
        }

        const isFilled = childElement.props.variant === undefined || childElement.props.variant === "filled";

        return <StateLayer
            borderRadius={childElement.props.borderRadius ?? radiuses.actions}
            overlayColor={isFilled ? "default" : getStateColorKey({
                type: childElement.props.type ?? "primary"
            })}
            borderWidth={borders.line}
            customTheme={customTheme}
            isPressed={true}
        />;
    };

    const flatStyle = StyleSheet.flatten<ViewStyle>(style);

    const isAnchorStretched = flatStyle?.alignSelf === "stretch" || flatStyle?.width !== undefined;

    return <View
        style={style}
    >
        <View
            {...webProps}
            style={[
                stylesheet.anchor,
                isAnchorStretched ? stylesheet.stretchedAnchor : undefined
            ]}
            collapsable={false}
            onLayout={onLayout}
            ref={anchorRef}
        >
            {
                cloneElement(childElement, {
                    ...isDisabled ? {
                        isDisabled: true
                    } : {},
                    "aria-controls": isOpen ? contentId : undefined,
                    id: childElement.props.id ?? triggerId,
                    "aria-expanded": isOpen,
                    "aria-haspopup": "menu",
                    onPress: handlePress
                })
            }
            {renderOpenLayer()}
        </View>
    </View>;
};
export default DropdownMenuTrigger;
